var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React, { Component } from 'react';
import { ButtonTheme, Placement } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { arrayOf, bool, eventListener, expression, extendComponent, list, number, record, shape, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { isContentfulString } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as AsteriskIcon } from '../../../resources/assets/components/DLS_Asterisk.svg';
import { ReactComponent as UploadIcon } from '../../../resources/assets/components/DLS_Upload.svg';
import { FileService } from '../../services/file-service/file-service.js';
import { Button } from '../button/button.js';
import { maxFileSizeShape } from '../file/file.js';
import { FileUploaderContentMode } from '../file-uploader-content/file-uploader-content-types.js';
import { FileUploaderContent } from '../file-uploader-content/file-uploader-content.js';
import { FileUploaderAPI } from './file-uploader-api.js';
import './file-uploader.css';
let FileUploader = class FileUploader extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            open: false,
            showErrorMessage: false,
        };
        this.onClick = () => {
            this.setState({ open: true });
        };
        this.onClose = () => {
            this.setState({ open: false });
        };
        this.renderRequiredBadge = () => {
            const { predefinedFiles = [] } = this.props;
            const { showErrorMessage } = this.state;
            if (!predefinedFiles.some((file) => file.required)) {
                return null;
            }
            return (React.createElement("div", { className: "c-file-uploader__required-file" },
                React.createElement("div", null,
                    MessageService.translate('tws.file-uploader.required'),
                    React.createElement(AsteriskIcon, { className: "c-file-uploader__icon" })),
                showErrorMessage ? React.createElement("span", { className: "c-file-uploader__error-message" }, MessageService.translate('tws.file-uploader.required.missing-file')) : null));
        };
        this.onRequiredFileError = (showErrorMessage) => {
            this.setState({ showErrorMessage });
        };
    }
    componentDidMount() {
        this.updateFileHandlerInstance();
    }
    componentDidUpdate(prevProps) {
        const { fileHandlerProviderName } = this.props;
        const { fileHandlerProviderName: prevFileHandlerProviderName } = prevProps;
        if (fileHandlerProviderName !== prevFileHandlerProviderName) {
            this.updateFileHandlerInstance();
        }
    }
    updateFileHandlerInstance() {
        const { fileHandlerProviderName } = this.props;
        const fileHandlerInstance = isContentfulString(fileHandlerProviderName)
            ? FileService.getFileHandlerInstance(fileHandlerProviderName)
            : FileService.getFileHandler();
        this.setState({ fileHandlerInstance });
    }
    renderFileUploaderContent(mode) {
        if (!this.state.fileHandlerInstance) {
            return null;
        }
        return (React.createElement(FileUploaderContent, { addDescriptionExpression: this.props.addDescriptionExpression, allowedTypes: this.props.allowedTypes, browseFileExpression: this.props.browseFileExpression, canAddDescription: this.props.canAddDescription, descriptionPlaceholderExpression: this.props.descriptionPlaceholderExpression, descriptionRequired: this.props.descriptionRequired, failedUploadExpression: this.props.failedUploadExpression, fileHandlerInstance: this.state.fileHandlerInstance, filesListTitleExpression: this.props.filesListTitleExpression, maxFileSize: this.props.maxFileSize, modalProps: this.props.modalProps, mode: mode, onAbort: this.props.onAbort, onBlur: this.props.onBlur, onChange: this.props.onChange, onClose: this.onClose, onCompleted: this.props.onCompleted, onError: this.props.onError, onFocus: this.props.onFocus, onInvalid: this.props.onInvalid, onPreview: this.props.onPreview, onRemove: this.props.onRemove, onRequiredFileError: this.onRequiredFileError, onRetry: this.props.onRetry, onUpload: this.props.onUpload, predefinedFiles: this.props.predefinedFiles, readOnly: this.props.readOnly, showMissingFiles: this.state.showErrorMessage, valid: this.props.valid, value: this.props.value }));
    }
    renderInline() {
        return this.renderFileUploaderContent(FileUploaderContentMode.NO_WRAPPER);
    }
    renderExternal() {
        const { open } = this.state;
        return (React.createElement(React.Fragment, null,
            this.renderButton(),
            open && this.renderFileUploaderContent(FileUploaderContentMode.MODAL_WRAPPER)));
    }
    renderButton() {
        const { addFileExpression } = this.props;
        return (React.createElement(React.Fragment, null,
            React.createElement(Button, { enabled: this.props.enabled && !this.props.readOnly, icon: React.createElement(UploadIcon, null), iconPlacement: Placement.RIGHT, onClick: this.onClick, theme: ButtonTheme.SECONDARY, value: addFileExpression }),
            this.renderRequiredBadge()));
    }
    render() {
        const { inline } = this.props;
        return (inline
            ? this.renderInline()
            : this.renderExternal());
    }
};
FileUploader.propTypes = {
    addDescriptionExpression: expression,
    addFileExpression: expression,
    allowedTypes: list,
    browseFileExpression: expression,
    canAddDescription: bool,
    descriptionPlaceholderExpression: expression,
    descriptionRequired: bool,
    enabled: bool,
    failedUploadExpression: expression,
    fileHandlerProviderName: string,
    filesListTitleExpression: expression,
    inline: bool,
    maxFileSize: maxFileSizeShape,
    modalProps: extendComponent('Modal', [
        'closeByDocument',
        'closeByEscape',
    ], [
        'children',
        'className',
        'closeable',
        'container',
        'contextData',
        'dialogDescription',
        'dockButtons',
        'focusOnOpen',
        'fragment',
        'hideHeaderOnMobile',
        'noPadding',
        'onBeforeClose',
        'onCancel',
        'onClose',
        'onSubmit',
        'theme',
        'title',
        'type',
        'uuid',
    ]),
    onAbort: eventListener,
    onBlur: eventListener,
    onChange: eventListener,
    onCompleted: eventListener,
    onError: eventListener,
    onFocus: eventListener,
    onInvalid: eventListener,
    onPreview: eventListener,
    onRemove: eventListener,
    onRetry: eventListener,
    onUpload: eventListener,
    /* eslint-disable-next-line sort-keys */
    predefinedFiles: record({ key: string, value: expression, required: bool }),
    readOnly: bool,
    valid: bool,
    value: arrayOf(shape({ filename: string, filepath: string, size: number })),
};
FileUploader.defaultProps = {
    addDescriptionExpression: 'tws.file-uploader.add-description-expression',
    addFileExpression: 'tws.file-uploader.add-file',
    allowedTypes: [],
    browseFileExpression: 'tws.file-uploader.browse',
    canAddDescription: false,
    descriptionPlaceholderExpression: 'tws.file-uploader.description-placeholder-expression',
    enabled: true,
    failedUploadExpression: 'tws.file-uploader.failed',
    fileHandlerProviderName: '',
    filesListTitleExpression: 'tws.file-uploader.files-list-title-expression',
    inline: false,
    modalProps: {
        closeByDocument: true,
        closeByEscape: true,
    },
    valid: true,
    value: [],
};
FileUploader = __decorate([
    AwesomeComponent({ api: FileUploaderAPI, type: ComponentType.DATA_ENTRY })
], FileUploader);
export { FileUploader, maxFileSizeShape, };
//# sourceMappingURL=file-uploader.js.map